import {
  AmortizationViewModel,
  StatementEntryItemType,
} from "@common/loans/statements/amortization.view-model";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { LoanModel } from "providers/loans/loan.model";
import { api } from "utils/api";

export interface LoanTransactionsFilters {
  loan?: LoanModel;
  dateRange?: {
    start?: string;
    end?: string;
  };
  excludeExitFee?: boolean;
}

const fetchTransactions = async (
  filters: LoanTransactionsFilters
): Promise<AmortizationViewModel | null> => {
  const response = await api.get<AmortizationViewModel | null>(
    `/loans/${filters.loan?.id}/transactions`,
    {
      params: {
        startDate: filters.dateRange?.start,
        endDate: filters.dateRange?.end,
      },
    }
  );

  if (response.status === 204) {
    return null;
  }

  const exitFee = filters.loan?.values.find(v => v.loan_field.identifier_key === "exit_fee");
  const expiryDate = moment(filters.loan?.funding_date ?? "").add(
    filters.loan?.duration ?? 0,
    "months"
  );

  const exitFeeValue = Number(exitFee?.value?.field ?? 0);
  const lastEntry = response.data?.entries[response.data?.entries.length - 1];

  if (!response.data) {
    return null;
  }

  let statement = response.data;

  if (
    !filters.excludeExitFee &&
    exitFeeValue > 0 &&
    lastEntry?.balance &&
    lastEntry.balance > 0 &&
    expiryDate.isSameOrAfter(moment(lastEntry?.date))
  ) {
    statement.entries.push({
      date: statement.endAt,
      description: exitFee?.loan_field.label ?? "Exit Fee",
      debit: exitFeeValue,
      credit: 0,
      balance: statement.remainingBalance + exitFeeValue,
      items: [
        {
          type: StatementEntryItemType.Fee,
          amount: exitFeeValue,
        },
      ],
    });
  }

  return statement;
};

export function useLoanTransactions({ loan, dateRange, excludeExitFee }: LoanTransactionsFilters) {
  return useQuery({
    queryKey: ["loans", "transactions", loan?.id, dateRange, excludeExitFee],
    queryFn: () => fetchTransactions({ loan, dateRange, excludeExitFee }),
    enabled: !!loan?.id,
  });
}
